import {
  alpha,
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
  useMediaQuery,
} from "@mui/material";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import theme, { responsiveFontSize } from "themes/Theme";
import HeaderComponent from "./_header";
import Sidebar from "./_aside";
import { useEffect, useState } from "react";
import UserLogoutDialog from "components/UserLogoutDialog/UserLogoutDialog";
import { navigationData } from "./navigationData";
import { useDispatch } from "react-redux";
import { Logo } from "assets/index";
import { getFromStore, StoreType } from "services/localStorage";

export const SIDEBAR_WIDTH = window.innerWidth * 0.2;

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

const DashboardLayout = () => {
  const navigate = useNavigate();
  const user: any = getFromStore(StoreType.AUTH);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [open, setOpen] = useState<boolean>(false);
  const [showLogoutModal, setShowLogoutModal] = useState<boolean>(false);

  useEffect(() => {
    if (
      user?.role &&
      (user?.role?.toLowerCase() === "admin" ||
        user?.role?.toLowerCase() === "super admin")
    ) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [user]);

  const toggleDrawer = () => {
    setOpen((prev) => !prev);
  };

  const showLogoutModalHandler = () => {
    setShowLogoutModal(true);
  };

  const handleLogout = () => {
    localStorage.clear();
    dispatch({ type: "RESET_ALL_STATE" });
    navigate("/auth/login", { replace: true });
  };

  return (
    <Box sx={styles.container}>
      {isMobile ? (
        <Drawer
          open={open}
          onClose={() => setOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper,.MuiDrawer-root": {
              boxSizing: "border-box",
              width: "70%",
              backgroundColor: "#FFFFFF",
              borderRight: `1px solid ${alpha(
                theme.palette.primary.main,
                0.3
              )}`,
            },
          }}
        >
          <DrawerHeader sx={{ justifyContent: "flex-start" }}>
            <Box
              component={"img"}
              src={Logo}
              alt="logo"
              sx={{ width: 60, height: 60 }}
            />
          </DrawerHeader>
          <List>
            {navigationData.map((text) => (
              <ListItemButton
                key={text.label}
                sx={{
                  backgroundColor:
                    pathname === text.path
                      ? alpha(theme.palette.primary.light, 0.1)
                      : "transparent",
                  ":hover": {
                    backgroundColor: alpha(theme.palette.primary.light, 0.1),
                  },
                  borderRadius: "1em",
                  mx: 1,
                  mb: 1,
                }}
                onClick={() => {
                  if (text.label === "Logout") {
                    setShowLogoutModal(true);
                  } else {
                    navigate(text.path);
                    setOpen(false); // ✅ Close the drawer after navigation
                  }
                }}
              >
                <ListItemIcon
                  sx={
                    pathname === text.path
                      ? styles.activeListIconItem
                      : styles.defaultListIconItem
                  }
                >
                  {text.icon}
                </ListItemIcon>
                <ListItemText
                  primary={text.label}
                  sx={
                    pathname === text.path
                      ? styles.activeListItem
                      : styles.defaultListItem
                  }
                />
              </ListItemButton>
            ))}
          </List>
        </Drawer>
      ) : (
        <Box component={"aside"} sx={styles.sidebarContainer}>
          <Sidebar showLogoutModal={showLogoutModalHandler} />
        </Box>
      )}

      <Box
        sx={{
          ...styles.mainContentContainer,
          width: isMobile ? "100%" : "82vw",
          paddingRight: isMobile ? "1em" : 0,
        }}
      >
        <HeaderComponent
          isMobile={isMobile}
          toggleDrawer={toggleDrawer}
          open={open}
        />
        <Box component={"main"} sx={styles.mainContent}>
          <Outlet />
        </Box>
      </Box>

      {showLogoutModal && (
        <UserLogoutDialog
          open={showLogoutModal}
          handleClose={() => setShowLogoutModal(false)}
          handleLogout={handleLogout}
        />
      )}
    </Box>
  );
};

export default DashboardLayout;

const styles = {
  container: {
    display: "flex",
    flexDirection: "row",
    height: "100vh",
    backgroundColor: theme.palette.background.paper,
    overflow: "hidden",
  },
  sidebarContainer: {
    width: SIDEBAR_WIDTH,
    backgroundColor: theme.palette.background.default,
    borderRadius: "2.5em",
    margin: "1em 0.5em 1em 1em",
    boxShadow:
      "rgba(0, 0, 0, 0.1) 0px 0px 5px 0px, rgba(0, 0, 0, 0.1) 0px 0px 1px 0px",
    padding: "1em",
  },
  mainContentContainer: {
    width: `calc(100% - ${SIDEBAR_WIDTH}px)`,
    margin: "1em 1em 1em 0.5em",
  },
  mainContent: {
    overflowY: "scroll",
    overflowX: "hidden",
    height: "100%",
    paddingBottom: "6.5em",
  },
  activeListItem: {
    color: theme.palette.primary.main,
    marginTop: "0.3em",
    alignItems: "center",
    fontSize: responsiveFontSize(12),
    "&:hover": {
      cursor: "pointer",
    },
  },
  defaultListItem: {
    marginTop: "0.3em",
    alignItems: "center",
    color: theme.palette.text.primary,
    fontSize: responsiveFontSize(12),
  },
  activeListIconItem: {
    color: theme.palette.primary.main,
  },
  defaultListIconItem: {
    color: theme.palette.text.primary,
  },
};
